/*
 * Flip-through book for the "Image with text" section.
 * Pages come from a product's portrait images (see sections/image-with-text.liquid).
 * The page-turn library (assets/page-flip.browser.js, StPageFlip, MIT) is only
 * loaded once the section comes close to the viewport.
 */
(() => {
  if (customElements.get('manual-flipbook')) return;

  let libPromise;
  const loadLib = (src) => {
    if (window.St && window.St.PageFlip) return Promise.resolve(window.St);
    libPromise =
      libPromise ||
      new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve(window.St);
        script.onerror = reject;
        document.head.appendChild(script);
      });
    return libPromise;
  };

  // "12_Modell_332_ohne_Text.jpg", "16_Ergaenzungsheft_326.jpg" -> [332, 12]
  // "Ergaenzungsheft_327-5.jpg" -> [327, 5]
  const pageKey = (name) => {
    let m = name.match(/^(\d{1,2})_(?:Modell|Ergaenzungsheft)_(\d{3})/i);
    if (m) return [Number(m[2]), Number(m[1])];
    m = name.match(/Ergaenzungsheft_(\d{3})-(\d{1,2})/i);
    if (m) return [Number(m[1]), Number(m[2])];
    return null;
  };

  // Newest supplement first, pages in order; unrecognised names keep their place at the end.
  const sortPages = (pages) =>
    pages
      .map((page, index) => ({ page, index, key: pageKey(page.name || '') }))
      .sort((a, b) => {
        if (a.key && b.key) return b.key[0] - a.key[0] || a.key[1] - b.key[1];
        if (a.key) return -1;
        if (b.key) return 1;
        return a.index - b.index;
      })
      .map((entry) => entry.page);

  class ManualFlipbook extends HTMLElement {
    connectedCallback() {
      if (this.observer || this.pageFlip) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.init().catch(() => this.classList.add('is-failed'));
        },
        { rootMargin: '600px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onTouchEnd) window.removeEventListener('touchend', this.onTouchEnd, { capture: true });
      if (this.onTouchCancel) window.removeEventListener('touchcancel', this.onTouchCancel, { capture: true });
      if (this.pageFlip) this.pageFlip.destroy();
      this.observer = null;
      this.pageFlip = null;
    }

    async init() {
      let pages = JSON.parse(this.querySelector('.manual-flipbook__pages').textContent);
      if (this.dataset.sort === 'true') pages = sortPages(pages);
      const max = parseInt(this.dataset.max, 10);
      if (max > 0) pages = pages.slice(0, max);
      if (pages.length < 2) throw new Error('Not enough pages');

      this.book = this.querySelector('.manual-flipbook__book');
      this.counter = this.querySelector('.manual-flipbook__counter');
      this.prevButton = this.querySelector('.manual-flipbook__nav--prev');
      this.nextButton = this.querySelector('.manual-flipbook__nav--next');

      this.images = pages.map((page) => {
        const item = document.createElement('div');
        item.className = 'manual-flipbook__page';
        const img = document.createElement('img');
        img.alt = page.alt || '';
        img.decoding = 'async';
        img.draggable = false;
        img.width = 900;
        img.height = Math.round(900 / (page.ratio || 0.7265));
        img.dataset.src = page.src;
        item.appendChild(img);
        this.book.appendChild(item);
        return img;
      });
      this.images.slice(0, 4).forEach((img) => img.setAttribute('fetchpriority', 'high'));
      this.loadAround(0);

      const St = await loadLib(this.dataset.lib);
      const ratio = 1944 / 2676;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.pageFlip = new St.PageFlip(this.book, {
        width: 500,
        height: Math.round(500 / ratio),
        size: 'stretch',
        minWidth: 230,
        maxWidth: 420,
        minHeight: 316,
        maxHeight: 578,
        usePortrait: true,
        showCover: false,
        autoSize: true,
        drawShadow: true,
        maxShadowOpacity: 0.35,
        showPageCorners: true,
        mobileScrollSupport: true,
        swipeDistance: 30,
        flippingTime: reducedMotion ? 250 : 750,
      });
      this.pageFlip.on('flip', (event) => {
        this.loadAround(event.data);
        this.update();
      });
      this.pageFlip.on('changeOrientation', () => this.update());
      // start loading the next pages as soon as a page is picked up, not only after the turn
      this.pageFlip.on('changeState', () => this.loadAround(this.pageFlip.getCurrentPageIndex()));
      this.pageFlip.loadFromHTML(this.book.querySelectorAll('.manual-flipbook__page'));

      this.prevButton.addEventListener('click', () => this.pageFlip.flipPrev());
      this.nextButton.addEventListener('click', () => this.pageFlip.flipNext());
      this.book.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.pageFlip.flipPrev();
        if (event.key === 'ArrowRight') this.pageFlip.flipNext();
      });
      this.guardTouches();

      this.classList.add('is-ready');
      this.update();
    }

    // After 250 ms the library treats any touch that did not drag the page as a tap and
    // flips towards the spot where the finger was lifted. That turns pages while people
    // scroll past the book, and slow sideways drags can go the wrong way. This listener
    // runs before the library's own touchend handler and settles those cases.
    guardTouches() {
      let start = null;
      this.book.addEventListener(
        'touchstart',
        (event) => {
          const touch = event.changedTouches[0];
          start = touch ? { x: touch.clientX, y: touch.clientY, time: Date.now() } : null;
        },
        { passive: true }
      );
      this.onTouchEnd = (event) => {
        const touch = event.changedTouches[0];
        const pageFlip = this.pageFlip;
        if (!start || !touch || !pageFlip) return;
        const dx = touch.clientX - start.x;
        const dy = touch.clientY - start.y;
        const quick = Date.now() - start.time < 250;
        start = null;
        if (pageFlip.isUserMove) return; // the page is being dragged; the library finishes the turn
        const horizontal = Math.abs(dx) > 30 && Math.abs(dx) > Math.abs(dy) * 1.5;
        if (horizontal && quick) return; // the library's own swipe detection handles this
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return; // a tap
        pageFlip.isUserTouch = false;
        if (horizontal) dx < 0 ? pageFlip.flipNext() : pageFlip.flipPrev();
      };
      this.onTouchCancel = () => {
        start = null;
        if (this.pageFlip) this.pageFlip.isUserTouch = false;
      };
      window.addEventListener('touchend', this.onTouchEnd, { capture: true, passive: true });
      window.addEventListener('touchcancel', this.onTouchCancel, { capture: true, passive: true });
    }

    loadAround(index) {
      for (let i = index - 4; i <= index + 8; i++) {
        const img = this.images[i];
        if (img && !img.getAttribute('src')) img.src = img.dataset.src;
      }
    }

    update() {
      if (!this.pageFlip) return;
      const index = this.pageFlip.getCurrentPageIndex();
      const count = this.pageFlip.getPageCount();
      const spread = this.pageFlip.getOrientation() === 'landscape';
      const last = spread ? Math.min(index + 2, count) : index + 1;
      this.counter.textContent = last > index + 1 ? `${index + 1}–${last} / ${count}` : `${index + 1} / ${count}`;
      this.prevButton.disabled = index <= 0;
      this.nextButton.disabled = last >= count;
      this.loadAround(index);
    }
  }

  customElements.define('manual-flipbook', ManualFlipbook);
})();
